/**
 * Test Candidate API Service
 * Handles test assignments for recruitment candidates
 *
 * Uses JWT-based API calls - candidateId is extracted from token on backend
 * No need to pass candidateId from frontend
 */

import http from '../client';
import { ENDPOINTS } from '../config';

// ============================================================================
// JWT-BASED FUNCTIONS (uses JWT token, candidateId extracted on backend)
// ============================================================================

// Simple cache for request deduplication
let testsCache = { all: null, pending: null, completed: null };
let testsCacheTime = { all: 0, pending: 0, completed: 0 };
const CACHE_TTL = 30000; // 30 seconds

// In-flight request tracking to prevent duplicate calls
const pendingRequests = new Map();

/**
 * Clear cache (call on logout or when needed)
 */
export const clearTestCache = () => {
  testsCache = { all: null, pending: null, completed: null };
  testsCacheTime = { all: 0, pending: 0, completed: 0 };
  pendingRequests.clear();
};

/**
 * Get all tests for current user (single API call via JWT)
 * @returns {Promise<Object>} { data: Array, error: null } or { data: [], error: Error }
 */
export const getMyTests = async () => {
  const now = Date.now();
  if (testsCache.all && (now - testsCacheTime.all) < CACHE_TTL) {
    return { data: testsCache.all, error: null };
  }

  if (pendingRequests.has('all')) {
    return pendingRequests.get('all');
  }

  const promise = (async () => {
    try {
      const response = await http.get(ENDPOINTS.TEST_CANDIDATE.MY_ALL);
      const data = response?.data || response || [];
      testsCache.all = data;
      testsCacheTime.all = Date.now();
      return { data, error: null };
    } catch (error) {
      console.error('Failed to get my tests:', error);
      return { data: [], error };
    }
  })();

  pendingRequests.set('all', promise);
  promise.finally(() => pendingRequests.delete('all'));
  return promise;
};

/**
 * Get pending tests (INVITED + IN_PROGRESS + PAUSED) for current user
 * Backend returns all non-completed statuses
 * @returns {Promise<Object>} { data: Array, error: null } or { data: [], error: Error }
 */
export const getMyPendingTests = async () => {
  const now = Date.now();
  if (testsCache.pending && (now - testsCacheTime.pending) < CACHE_TTL) {
    return { data: testsCache.pending, error: null };
  }

  if (pendingRequests.has('pending')) {
    return pendingRequests.get('pending');
  }

  const promise = (async () => {
    try {
      const response = await http.get(ENDPOINTS.TEST_CANDIDATE.MY_PENDING);
      const data = response?.data || response || [];
      testsCache.pending = data;
      testsCacheTime.pending = Date.now();
      return { data, error: null };
    } catch (error) {
      console.error('Failed to get my pending tests:', error);
      return { data: [], error };
    }
  })();

  pendingRequests.set('pending', promise);
  promise.finally(() => pendingRequests.delete('pending'));
  return promise;
};

/**
 * Get completed tests for current user (single API call via JWT)
 * @returns {Promise<Object>} { data: Array, error: null } or { data: [], error: Error }
 */
export const getMyCompletedTests = async () => {
  const now = Date.now();
  if (testsCache.completed && (now - testsCacheTime.completed) < CACHE_TTL) {
    return { data: testsCache.completed, error: null };
  }

  if (pendingRequests.has('completed')) {
    return pendingRequests.get('completed');
  }

  const promise = (async () => {
    try {
      const response = await http.get(ENDPOINTS.TEST_CANDIDATE.MY_COMPLETED);
      const data = response?.data || response || [];
      testsCache.completed = data;
      testsCacheTime.completed = Date.now();
      return { data, error: null };
    } catch (error) {
      console.error('Failed to get my completed tests:', error);
      return { data: [], error };
    }
  })();

  pendingRequests.set('completed', promise);
  promise.finally(() => pendingRequests.delete('completed'));
  return promise;
};

// ============================================================================
// ASSESSMENT PLAYER ID BASED FUNCTIONS (legacy)
// ============================================================================

/**
 * Get all tests for a candidate (assessment player)
 * @param {number} assessmentPlayerId
 * @returns {Promise<Object>} { data: Array, error: null } or { data: [], error: Error }
 */
export const getTestsForCandidate = async (assessmentPlayerId) => {
  try {
    const response = await http.get(ENDPOINTS.TEST_CANDIDATE.BY_AP(assessmentPlayerId));
    return { data: response?.data || response || [], error: null };
  } catch (error) {
    console.error('Failed to get tests for candidate:', error);
    return { data: [], error };
  }
};

/**
 * Get pending (INVITED) tests for a candidate
 * @param {number} assessmentPlayerId
 * @returns {Promise<Object>} { data: Array, error: null } or { data: [], error: Error }
 */
export const getPendingTests = async (assessmentPlayerId) => {
  try {
    const response = await http.get(ENDPOINTS.TEST_CANDIDATE.PENDING(assessmentPlayerId));
    return { data: response?.data || response || [], error: null };
  } catch (error) {
    console.error('Failed to get pending tests:', error);
    return { data: [], error };
  }
};

/**
 * Get completed tests for a candidate
 * @param {number} assessmentPlayerId
 * @returns {Promise<Object>} { data: Array, error: null } or { data: [], error: Error }
 */
export const getCompletedTests = async (assessmentPlayerId) => {
  try {
    const response = await http.get(ENDPOINTS.TEST_CANDIDATE.COMPLETED(assessmentPlayerId));
    return { data: response?.data || response || [], error: null };
  } catch (error) {
    console.error('Failed to get completed tests:', error);
    return { data: [], error };
  }
};

/**
 * Get tests for a candidate at a specific recruitment stage
 * @param {number} assessmentPlayerId
 * @param {number} stageId
 * @returns {Promise<Object>} { data: Array, error: null } or { data: [], error: Error }
 */
export const getTestsAtStage = async (assessmentPlayerId, stageId) => {
  try {
    const response = await http.get(ENDPOINTS.TEST_CANDIDATE.BY_STAGE(assessmentPlayerId, stageId));
    return { data: response?.data || response || [], error: null };
  } catch (error) {
    console.error('Failed to get tests at stage:', error);
    return { data: [], error };
  }
};

/**
 * Get a single test assignment by ID
 * @param {number} testCandidateId
 * @returns {Promise<Object>} { data: Object, error: null } or { data: null, error: Error }
 */
export const getTestCandidate = async (testCandidateId) => {
  try {
    const response = await http.get(ENDPOINTS.TEST_CANDIDATE.GET(testCandidateId));
    return { data: response?.data || response || null, error: null };
  } catch (error) {
    console.error('Failed to get test candidate:', error);
    return { data: null, error };
  }
};

/**
 * Get a test assignment by secure access token (UUID)
 * Used for direct URL access: /test/{accessToken}
 * @param {string} accessToken - UUID string
 * @returns {Promise<Object>} { data: Object, error: null } or { data: null, error: Error }
 */
export const getTestCandidateByAccessToken = async (accessToken) => {
  try {
    const response = await http.get(ENDPOINTS.TEST_CANDIDATE.BY_ACCESS_TOKEN(accessToken));
    return { data: response?.data || response || null, error: null };
  } catch (error) {
    console.error('Failed to get test candidate by access token:', error);
    return { data: null, error };
  }
};

/**
 * Authenticate directly using test access token
 * This auto-authenticates the user based on their phone/email in the test invitation
 * @param {string} accessToken - UUID string from test URL
 * @returns {Promise<Object>} { data: { accessToken, testCandidateId, requiresPhoneInput, ... }, error: null }
 */
export const authenticateByTestAccess = async (accessToken) => {
  try {
    const response = await http.post(ENDPOINTS.AUTH.TEST_ACCESS(accessToken), null, { skipAuth: true });
    return { data: response?.data || response || null, error: null };
  } catch (error) {
    console.error('Failed to authenticate by test access:', error);
    return { data: null, error };
  }
};

/**
 * Authenticate using test access token with phone number input
 * Used when the candidate's phone is not stored in the system
 * @param {string} accessToken - UUID string from test URL
 * @param {string} phoneNumber - Phone number provided by the candidate
 * @returns {Promise<Object>} { data: { accessToken, testCandidateId, ... }, error: null }
 */
export const authenticateByTestAccessWithPhone = async (accessToken, phoneNumber) => {
  try {
    const response = await http.post(
      `${ENDPOINTS.AUTH.TEST_ACCESS(accessToken)}/phone?phoneNumber=${encodeURIComponent(phoneNumber)}`,
      null,
      { skipAuth: true }
    );
    return { data: response?.data || response || null, error: null };
  } catch (error) {
    console.error('Failed to authenticate by test access with phone:', error);
    return { data: null, error };
  }
};

// ============================================================================
// CANDIDATE ID BASED FUNCTIONS (for Diil app after phone/telegram login)
// ============================================================================

/**
 * Get all tests for a candidate by candidateId
 * @param {number} candidateId
 * @returns {Promise<Object>} { data: Array, error: null } or { data: [], error: Error }
 */
export const getTestsByCandidateId = async (candidateId) => {
  try {
    const response = await http.get(ENDPOINTS.TEST_CANDIDATE.BY_CANDIDATE(candidateId));
    return { data: response?.data || response || [], error: null };
  } catch (error) {
    console.error('Failed to get tests by candidateId:', error);
    return { data: [], error };
  }
};

/**
 * Get pending (INVITED) tests for a candidate by candidateId
 * @param {number} candidateId
 * @returns {Promise<Object>} { data: Array, error: null } or { data: [], error: Error }
 */
export const getPendingTestsByCandidateId = async (candidateId) => {
  try {
    const response = await http.get(ENDPOINTS.TEST_CANDIDATE.PENDING_BY_CANDIDATE(candidateId));
    return { data: response?.data || response || [], error: null };
  } catch (error) {
    console.error('Failed to get pending tests by candidateId:', error);
    return { data: [], error };
  }
};

/**
 * Get completed tests for a candidate by candidateId
 * @param {number} candidateId
 * @returns {Promise<Object>} { data: Array, error: null } or { data: [], error: Error }
 */
export const getCompletedTestsByCandidateId = async (candidateId) => {
  try {
    const response = await http.get(ENDPOINTS.TEST_CANDIDATE.COMPLETED_BY_CANDIDATE(candidateId));
    return { data: response?.data || response || [], error: null };
  } catch (error) {
    console.error('Failed to get completed tests by candidateId:', error);
    return { data: [], error };
  }
};

/**
 * Get tests for a candidate at a specific stage by candidateId
 * @param {number} candidateId
 * @param {number} stageId
 * @returns {Promise<Object>} { data: Array, error: null } or { data: [], error: Error }
 */
export const getTestsByCandidateIdAtStage = async (candidateId, stageId) => {
  try {
    const response = await http.get(ENDPOINTS.TEST_CANDIDATE.BY_CANDIDATE_STAGE(candidateId, stageId));
    return { data: response?.data || response || [], error: null };
  } catch (error) {
    console.error('Failed to get tests at stage by candidateId:', error);
    return { data: [], error };
  }
};

// ============================================================================
// TEST PROGRESS FUNCTIONS
// ============================================================================

/**
 * Start a test (marks status as IN_PROGRESS)
 * @param {number} testCandidateId
 * @returns {Promise<Object>}
 */
export const startTest = async (testCandidateId) => {
  try {
    const response = await http.post(ENDPOINTS.TEST_CANDIDATE.START(testCandidateId));
    return { data: response?.data || response, error: null };
  } catch (error) {
    console.error('Failed to start test:', error);
    return { data: null, error };
  }
};

/**
 * Complete a test with score
 * @param {number} testCandidateId
 * @param {number} score
 * @param {number} percentage
 * @returns {Promise<Object>}
 */
export const completeTest = async (testCandidateId, score, percentage) => {
  try {
    const params = new URLSearchParams();
    if (score !== undefined) params.append('score', score);
    if (percentage !== undefined) params.append('percentage', percentage);

    const paramStr = params.toString();
    const url = paramStr
      ? `${ENDPOINTS.TEST_CANDIDATE.COMPLETE(testCandidateId)}?${paramStr}`
      : ENDPOINTS.TEST_CANDIDATE.COMPLETE(testCandidateId);
    const response = await http.post(url);
    return { data: response?.data || response, error: null };
  } catch (error) {
    console.error('Failed to complete test:', error);
    return { data: null, error };
  }
};

/**
 * Pause a test (marks status as PAUSED for resume later)
 * @param {number} testCandidateId
 * @returns {Promise<Object>}
 */
export const pauseTest = async (testCandidateId) => {
  try {
    const response = await http.post(ENDPOINTS.TEST_CANDIDATE.PAUSE(testCandidateId));
    return { data: response?.data || response, error: null };
  } catch (error) {
    console.error('Failed to pause test:', error);
    return { data: null, error };
  }
};

export default {
  // JWT-based (no IDs needed)
  getMyTests,
  getMyPendingTests,
  getMyCompletedTests,
  clearTestCache,
  // Assessment player ID based
  getTestsForCandidate,
  getPendingTests,
  getCompletedTests,
  getTestsAtStage,
  getTestCandidate,
  getTestCandidateByAccessToken,
  // Candidate ID based
  getTestsByCandidateId,
  getPendingTestsByCandidateId,
  getCompletedTestsByCandidateId,
  getTestsByCandidateIdAtStage,
  // Test progress
  startTest,
  completeTest,
  pauseTest,
  // Direct authentication
  authenticateByTestAccess,
  authenticateByTestAccessWithPhone
};
